<script setup lang="ts">
/**
 * BG §22.4 — the input. Near-black ground, dirty-white hairline border, cyan border and glow on focus.
 *
 * BG §22.3 requires a real label even when it is visually hidden, so `label` is required rather than
 * optional: a caller cannot accidentally ship an unlabelled field. `invalid` sets `aria-invalid` and
 * wires `aria-describedby` to the caller's message element, so the error reaches a screen reader
 * instead of being communicated by border colour alone.
 */
const model = defineModel<string>({ required: true })

withDefaults(
    defineProps<{
        /** Always rendered; `labelHidden` only removes it visually. */
        label: string
        id: string
        type?: string
        placeholder?: string
        autocomplete?: string
        invalid?: boolean
        /** id of the element describing the current error or hint. */
        describedBy?: string
        labelHidden?: boolean
        required?: boolean
    }>(),
    {
        type: 'text',
        invalid: false,
        labelHidden: true,
        required: false,
    },
)
</script>

<template>
    <div class="zh-input">
        <label :for="id" :class="labelHidden ? 'zh-input__label--hidden' : 'zh-input__label'">
            {{ label }}
        </label>
        <input
            :id="id"
            v-model="model"
            class="zh-input__field"
            :type="type"
            :placeholder="placeholder"
            :autocomplete="autocomplete"
            :required="required"
            :aria-invalid="invalid ? 'true' : undefined"
            :aria-describedby="describedBy"
        />
    </div>
</template>

<style scoped>
.zh-input {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    width: 100%;
}

.zh-input__label {
    font-family: var(--font-mono);
    font-size: 0.75rem;
    letter-spacing: var(--tracking-system);
    text-transform: uppercase;
    color: var(--muted-grey);
}

/* Visually hidden but reachable: the pattern BG §22.3 asks for. */
.zh-input__label--hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
    border: 0;
}

.zh-input__field {
    width: 100%;
    min-height: 3.25rem;
    padding: 0 1rem;
    border: 1px solid var(--input);
    border-radius: 0;
    background: color-mix(in srgb, var(--void-black) 75%, transparent);
    color: var(--dirty-white);
    font-family: var(--font-mono);
    font-size: 0.875rem;
    letter-spacing: 0.02em;
    transition:
        border-color var(--motion-ui) ease-out,
        box-shadow var(--motion-ui) ease-out;
}

.zh-input__field::placeholder {
    color: var(--muted-grey);
    text-transform: uppercase;
    letter-spacing: var(--tracking-system);
    font-size: 0.8125rem;
}

.zh-input__field:focus {
    outline: none;
    border-color: var(--electric-cyan);
    box-shadow: var(--glow-cyan);
}

/* Keyboard users still get a visible ring if the glow is ever lost to a forced-colours mode. */
.zh-input__field:focus-visible {
    outline: 1px solid var(--electric-cyan);
    outline-offset: 1px;
}

.zh-input__field[aria-invalid='true'] {
    border-color: var(--warning-amber);
}

.zh-input__field[aria-invalid='true']:focus {
    box-shadow: 0 0 1.5rem color-mix(in srgb, var(--warning-amber) 45%, transparent);
}

@media (prefers-reduced-motion: reduce) {
    .zh-input__field {
        transition: none;
    }
}
</style>
